import { SnackbarOrigin } from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridValidRowModel,
} from "@mui/x-data-grid";
import {
  MutationOptions,
  UseQueryResult,
  useMutation,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { MData } from "../components/MaskMultiSelect";
import { STATE_RECORD } from "../constants";
import { Address } from "../constants/ProviderGroupFormInitialValues";
import { SESSION_EXPIRED_MESSAGE } from "../constants/auth-constant";
import { Days, Option, PickedWeekDayTimes } from "../constants/common";
import { PROVIDER_PATHS } from "../constants/paths";
import { useLoadingContext } from "../context/LoadingProvider";
import {
  Configuration,
  LocationHour,
  LocationHourDayOfWeekEnum,
} from "../services";
import { getAccessToken, getStoredRealm, getViewAs } from "./auth-storage";
import { toLocalTimeConverter } from "./date-time-utility";
import { getTenantFromHostname } from "./get-tenant-from-hostname";

export function replaceUnderScoreAddSpace(name: string) {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .replace(/(?:^|\s)(\w)/g, (match) => match.toUpperCase());
}

export function formatClaimType(name?: string) {
  if (!name) return "";
  return replaceUnderScoreAddSpace(name)
    .replace(/\s*Claim$/i, "")
    .trim();
}

export type SelectItem = {
  value: string;
  label: string;
  selected?: boolean;
};

/**
 *  To apply default config settings to TABLE columns
 *  defaultSetting promote to change once - reflect everywhere
 *
 * @param columns
 * @returns
 *
 * @author rajkumar rathod
 */
export const applyDefaultColumnSettings = <T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  sortableField?: string[]
): GridColDef<T>[] =>
  columns.map((column) => ({
    ...column,
    sortable: sortableField?.includes(column?.field),
    disableColumnMenu: true,
    headerClassName: "header-theme",
  }));

export function getConfigObject() {
  const config = new Configuration({
    accessToken: getAccessToken(),
  });
  return config;
}

type PaginatedResult<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  content: T[];
};
export type Result<T> = {
  data: T;
};
/**
 * page Keys which are return while api call
 */
type PageKey = "number" | "size" | "totalElements" | "totalPages";

/**
 * page type when api success mapped with key and type number
 */
export type Page = { [k in PageKey]: number };

/**
 *  create custom type use for get API call for table
 */
export type PaginatedContent<T> = {
  data: { content: T[]; page: Page };
  requestId: string;
  message: string;
};
/**
 * combine type to reduce code snippet of `useQueryResult`
 */
export type useBaseContent<T> = UseQueryResult<PaginatedContent<T>, AxiosError>;

export type useBaseResult<T> = UseQueryResult<PaginatedResult<T>, AxiosError>;
export type useBaseData<T> = UseQueryResult<Result<T>, AxiosError>;
export type DGT_Pagination = GridPaginationModel;

/**type for param `getPageIndexWhileSearch` */
type GetPaginationWhileSearchParam = {
  pagination: DGT_Pagination;
  searchKey?: string;
  prevPage: React.MutableRefObject<number>;
};

/**
 * This utility design to handle the error while search
 *
 * @warning MUST USE keepPreviousData as placeholderData in useQuery
 *
 * @warning MUST USE keepPreviousData as placeholderData in useQuery
 *
 * This will reduce the overhead of checking search and modify pageIndex everyTime
 *
 * @param pagination (DGT_Pagination)
 * @returns modified pageIndex
 */
export const getPageIndexWhileSearch = ({
  pagination,
  searchKey,
  prevPage,
}: GetPaginationWhileSearchParam): DGT_Pagination["page"] => {
  if (searchKey && searchKey !== "" && prevPage.current === pagination.page) {
    return 0;
  }

  /**
   * if page index change then allow the page to change and set curPage to prevPage for next cycle
   */
  prevPage.current = pagination.page;
  return pagination.page;
};

export type useBaseQuery<T> = UseQueryResult<PaginatedResult<T>, AxiosError>;

/**
 * The generated client types every response envelope's `data` as `object`, so a
 * hook's payload has to be named at the SDK boundary. These state it once
 * instead of repeating the cast in every query hook.
 */
export const asPaginatedContent = <T>(envelope?: object) =>
  envelope as PaginatedContent<T>;

export const asResult = <T>(envelope?: object) => envelope as Result<T>;

export function getAge(dateOfBirth: string) {
  if (!dateOfBirth) return "";

  // A DOB is a calendar date: read its date part so no timezone shifts the day.
  const isoDate = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const parsed = new Date(dateOfBirth);
  const [year, month, day] = isoDate
    ? isoDate.slice(1).map(Number)
    : [parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate()];
  const today = new Date();

  let months =
    (today.getFullYear() - year) * 12 + (today.getMonth() + 1 - month);
  if (today.getDate() < day) months--;

  if (months >= 12) return Math.floor(months / 12);
  if (months >= 1) return `${months} ${months === 1 ? "month" : "months"}`;

  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const days = Math.round(
    (startOfToday.getTime() - new Date(year, month - 1, day).getTime()) /
      86_400_000
  );
  // Also false for NaN, so an invalid or future DOB shows no age.
  if (!(days >= 0)) return "";
  return `${days} ${days === 1 ? "day" : "days"}`;
}

/**
 * created this const to support loose coupling while it may replace in future with user default pageSize
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Smaller page size for performance-sensitive listings (e.g. LOINC catalog, ERA/EOB)
 * where larger record fetches degrade responsiveness.
 */
export const SMALL_PAGE_SIZE = 10;

/**
 * Standard page size options for paginated tables across the platform.
 */
export const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 50, 100];

/**
 * To handle the conversion of Blob file
 * @param file
 * @returns  promise<base64File-`string` >
 */
export const convertToBase64 = (file: File): Promise<string> =>
  new Promise((res, rej) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      const result = fileReader.result as string; // Cast to string
      res(result.split("base64,")[1] as string); // Split and return the base64 part
    };
    fileReader.onerror = (error) => {
      rej(error);
    };
  });

/**
 * This Fn work like template for loading handle loading state
 * this will reduce the overhead for manage the loading state
 *
 * @param mutationFn
 * @param options
 *
 * @author rajkumar rathod
 */
export const useMutationWithLoading = <TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: MutationOptions<TData, unknown, TVariables>
) => {
  const { setIsLoading } = useLoadingContext();

  return useMutation<TData, unknown, TVariables>({
    ...options,
    mutationFn: async (variables: TVariables) => {
      setIsLoading(true);
      return await mutationFn(variables).finally(() => setIsLoading(false));
    },
  });
};

/**
 * utility to transform data list to obj
 * transform data to backend type {k:v,k:v}
 *
 * @author rajkumar rathod
 */
export const transformMultiSelectBackEndVal = <T extends object>(
  opns?: T[]
) => {
  /** return null while no options selected */
  if (!opns || opns?.length <= 0) return null;

  const keys = Object.keys(opns[0]) as (keyof T)[];
  const copiedVals = [...opns];

  /**
   * transform data to backend type {k:v,k:v}
   */
  return copiedVals?.reduce((acc, curVal) => {
    if (curVal) acc[curVal[keys[0]]] = curVal[keys[1]];
    return acc;
  }, {} as any);
};

/**
 * utility to transform data obj to list
 * transform data to frontEnd type as [{values:"",label:""}]
 *
 * @param {[{values:"",label:""}]} keysList
 *
 * @param {{[k]:string}} opns
 *
 * @author rajkumar rathod
 */
export const transformMultiSelectFrontEndVal = <T extends object>(
  keysList?: T[],
  opns?: Record<string, string> | null
): MData[] | undefined => {
  if (!keysList?.length) return;

  /** check is selected options are available */
  const isOptions = Object.keys(opns ?? {})?.length > 0;
  const keys =
    keysList?.length > 0 ? (Object.keys(keysList[0]) as (keyof T)[]) : [];

  /** return undefined while no options selected */
  if (!isOptions) return;

  const copiedVals = cloneDeep(opns);

  return Object.entries(copiedVals ?? {})?.map(([val, label]) => ({
    [keys[0]]: val,
    [keys[1]]: label,
  })) as MData[];
};

// const DEFAULT_MSG = "something went wrong";

export type SnackbarInterface = {
  type: "success" | "error";
  message: string;
  duration?: number;
  position?: SnackbarOrigin;
  traceId?: string;
};

export function getSuccessSnackbar(res: any): SnackbarInterface {
  const message = res?.message || res?.data?.message;
  return {
    type: "success",
    message: typeof message === "string" ? message : "",
  };
}

export function getErrorSnackbar(_err: any): SnackbarInterface {
  const err = _err?.response?.data;
  const statusCode = _err?.response?.status;

  const getMessage = () => {
    if (statusCode === 500)
      return { message: err?.userMessage, traceId: err?.requestId };
    else if (statusCode === 401) return { message: SESSION_EXPIRED_MESSAGE };

    return { message: err?.message };
  };

  return {
    type: "error",
    ...getMessage(),
  };
}

/**
 * @warn it is just to follow dry and reduce transform data for MultiSelect
 *
 * @param k keys in data
 * @param data actual data
 *
 * @returns un necessary transform data for multiSelect
 *
 * @author rajkumar rathod
 */
export function getUnNecessaryTransformData<T extends Record<string, any>>(
  k: Array<keyof T>,
  data: T
): T {
  if (!k) return data;

  const copiedData = cloneDeep(data);
  k.forEach((key) => {
    if (key in copiedData) {
      copiedData[key] = transformMultiSelectBackEndVal(copiedData[key]);
    }
  });

  return copiedData;
}

export function formatPhoneNumber(number: string) {
  // Ensure the number is treated as a string
  const str = number.toString();

  // Replace the string with the desired format
  const formatted = str.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

  return formatted;
}

/**
 * Format a monetary value for display.
 *
 * Empty / null / non-numeric amounts render as `$ 0.00` instead of a dash, so
 * money columns always read as money. Values are always shown with 2 decimals.
 *
 * @param value raw amount coming from the API (number or numeric string)
 */
export function formatMoney(value: unknown) {
  const n = Number(value);
  const amount = value === null || value === undefined || value === "" ? 0 : n;

  return `$ ${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
}

/**
 * Format a monetary value for an input that renders its own `$` adornment:
 * always 2 decimals, with an empty value left empty so the field falls back to
 * its placeholder.
 *
 * Table cells want `$ 0.00` for an empty amount instead — use formatMoney().
 *
 * @param value raw amount coming from the API (number or numeric string)
 */
export function toTwoDecimals(value: unknown) {
  if (value === null || value === undefined || value === "") return "";

  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "";
}

/**
 * format the  address into string and as per the application standard
 * @param Address add
 */
export function getAddressFormat(add?: Address) {
  if (!add) return "";

  const { line1, line2, city, state, zipcode, country } = add;

  const formattedAddress = [
    line1,
    line2,
    city,
    state ? STATE_RECORD[state] : "",
    country,
    zipcode,
  ]
    .filter(Boolean) // Removes any empty or undefined values
    .join(", "); // Joins the non-empty values with a comma

  return formattedAddress;
}

// default list to transform multiSelect data
export const DEFAULT_UUID_NAME_LIST = [
  {
    uuid: "",
    name: "",
  },
];

export const DEFAULT_VALUE_LABEL_LIST = [{ value: "", label: "" }];
export const DEFAULT_INS_LABEL_LIST = [
  {
    payerId: "",
    payerName: "",
  },
];

export const DEF_PAGINATION = {
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

/**
 * Default pagination for performance-sensitive listings (LOINC catalog, ERA/EOB)
 * that should keep the smaller page size.
 */
export const DEF_PAGINATION_SMALL = {
  page: 0,
  pageSize: SMALL_PAGE_SIZE,
};

export const DEFAULT_PROVIDER_GROUP_IMG_SRC =
  "/default-images/provider group.svg";
export const DEFAULT_USER_SRC = "/default-images/user.svg";

// default fontsize for textField/multiselect and customSelect
/** to support loose coupling */

export const DEFAULT_FONTSIZE = { fontSize: "14px" };

export const FILED_LABEL_FONTSIZE = { fontSize: "15px" };

export const ICON_SIZE = "20px";
export const DEFAULT_LABEL_COLOR = "#21262B";
export const DEFAULT_LABEL_COLOR_GREEN = "#049B22";
export const DEFAULT_BG_LABEL_COLOR_GREEN = "#E5F8E7";
export const DEFAULT_LABEL_COLOR_RED = "#FF0000";
export const DEFAULT_LABEL_COLOR_2 = "#74797B";
export const DEFAULT_ICON_COLOR = "#4d4d4d";
export const DEFAULT_KEY_LABEL_COLOR = "#595F63";
export const DEFAULT_BG_COLOR = "#f2f6f8";
export const DEFAULT_BG_COLOR_2 = "#F5F8FA";
export const DEFAULT_BG_LABEL_COLOR_INACTIVE = "#FFF4E4";
export const DEFAULT_LABEL_COLOR_INACTIVE = "#FFAA00";
export const DEFAULT_BLUE_TEXT_COLOR = "#104E84";

/** Secondary text color for dark mode */
export const DEFAULT_TEXT_COLOR = "#0E151D";
export const DEFAULT_SECONDARY_TEXT_COLOR = "#373D41";
export const DEFAULT_BORDER_COLOR = "#E7E7E7";
export const DEFAULT_NEUTRAL_TEXT_COLOR = "#595F63";
export const DEFAULT_TEXT_FONT_WEIGHT = "400";

/** Purple accent color pair for chips, badges, and highlights. Use textColor and backgroundColor together for consistency. */
export const PURPLE_ACCENT_COLORS = {
  textColor: "#4C1AA3",
  backgroundColor: "#ECE4FA",
  iconColor: "#4C1AA3",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

/** Blue accent color pair for chips, badges, and highlights. Use textColor and backgroundColor together for consistency. */
export const BLUE_ACCENT_COLORS = {
  textColor: "#004AB1",
  backgroundColor: "#E0EDFF",
  iconColor: "#004AB1",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

export const GREEN_ACCENT_COLORS = {
  textColor: "#049B22",
  backgroundColor: "#E1FCDE",
  iconColor: "#049B22",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

export const LIGHT_GREEN_ACCENT_COLORS = {
  textColor: "#06988C",
  backgroundColor: "#EAFFFD",
  iconColor: "#06988C",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

export const GRAY_ACCENT_COLORS = {
  textColor: "#595F63",
  backgroundColor: "#E7E7E7",
  iconColor: "#595F63",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

export const ORANGE_ACCENT_COLORS = {
  textColor: "#DB5903",
  backgroundColor: "#FFF3EB",
  iconColor: "#DB5903",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

export const RED_ACCENT_COLORS = {
  textColor: "#B1000F",
  backgroundColor: "#FFF2F3",
  iconColor: "#B1000F",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

export const YELLOW_ACCENT_COLORS = {
  textColor: "#DBA103",
  backgroundColor: "#FFFAEB",
  iconColor: "#DBA103",
  borderRadius: "16px !important",
  padding: "2px 5px 2px 5px",
} as const;

// --- Notification colors (patient portal notification UI) ---
export const NOTIFICATION_COLORS = {
  // Badge (navbar bell icon)
  BADGE_BG: "#E1FCDE",
  BADGE_TEXT: "#17BF33",
  // Tabs
  TAB_ACTIVE_TEXT: "#104E84",
  TAB_INACTIVE_TEXT: "#595F63",
  TAB_ACTIVE_BADGE_BG: "#AEDAFF",
  TAB_ACTIVE_BADGE_TEXT: "#0B406E",
  TAB_INACTIVE_BADGE_BG: "#E7E7E7",
  TAB_INACTIVE_BADGE_TEXT: "#21262B",
  TAB_INDICATOR: "#104E84",
  // Card
  CARD_UNREAD_BG: "#F2F6F8",
  CARD_UNREAD_BORDER: "#E3ECEF",
  CARD_READ_BG: "#FFFFFF",
  CARD_READ_BORDER: "#E7E7E7",
  CARD_TITLE_TEXT: "#373D41",
  CARD_TIME_TEXT: "#595F63",
  CARD_UNREAD_DOT: "#2771B1",
  // Icon circle
  ICON_CIRCLE_BG: "#E5F3FF",
  ICON_COLOR: "#104E84",
  // Section
  BORDER: "#E7E7E7",
  DATE_LABEL_TEXT: "#21262B",
} as const;

export const NAVBAR = {
  TAB_FONT_SIZE: "12px",
  TAB_COLOR: "#FCFCFC",
  TAB_GAP: "16px",
  EXPAND_ICON_COLOR: "#FCFCFC",
  EXPAND_ICON_SIZE: 16,
  MENU_ITEM_FONT_SIZE: "12px",
  MENU_ITEM_COLOR: "#1A1A1A99",
  AVATAR_BG: "#E0E0E0",
  AVATAR_COLOR: "#292929",
  AVATAR_SIZE: 28,
  AVATAR_FONT_SIZE: 12,
  AVATAR_LARGE_SIZE: "70px",
  AVATAR_LARGE_FONT_SIZE: 24,
  LOGO_WIDTH: 125,
  LOGO_HEIGHT: 36,
} as const;

/**
 *
 * @param officeHours transform BackEnd office-hours
 * @author rajkumar rathod
 */
export const unNecessaryBEOfficeHourTrans = (
  officeHours?: PickedWeekDayTimes
) => {
  if (!officeHours) return;
  const transformDays: Array<LocationHour> = [];
  Object.entries(officeHours)?.forEach(([k, v]) => {
    v?.forEach((it) => {
      const newDay: LocationHour = {
        dayOfWeek: DAY_MAPPER[k as keyof typeof Days],
        openingTime: it.open!,
        closingTime: it.close!,
      };

      transformDays.push(newDay);
    }, []);
  });

  return transformDays;
};

/**
 *
 * @param officeHours transform FrontEnd office-hours
 * @author rajkumar rathod
 */
export const unNecessaryFEOfficeHourTrans = (officeHours?: LocationHour[]) => {
  if (!officeHours) return;

  return officeHours.reduce(
    (acc, { dayOfWeek, openingTime, closingTime }: LocationHour) => {
      const mappedDay: keyof typeof Days = REVERSE_DAY_MAPPER[dayOfWeek ?? ""];
      acc[mappedDay] = acc[mappedDay] || [];

      acc[mappedDay]?.push({ open: openingTime, close: closingTime });

      return acc;
    },
    {} as PickedWeekDayTimes
  );
};

const DAY_MAPPER: Record<keyof typeof Days, LocationHourDayOfWeekEnum> = {
  monday: "MONDAY",
  tuesday: "TUESDAY",
  wednesday: "WEDNESDAY",
  thursday: "THURSDAY",
  friday: "FRIDAY",
  saturday: "SATURDAY",
  sunday: "SUNDAY",
};

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const REVERSE_DAY_MAPPER: Record<string, keyof typeof Days> = {
  SUNDAY: "sunday",
  MONDAY: "monday",
  TUESDAY: "tuesday",
  WEDNESDAY: "wednesday",
  THURSDAY: "thursday",
  FRIDAY: "friday",
  SATURDAY: "saturday",
};

/**
 * @description to handle the view for office hours in different sections
 * @param officeHours
 * @returns {Array<string>}
 *
 * @author rajkumar rathod
 */
export const transformOfficeHoursToView = (officeHours?: LocationHour[]) => {
  if (!officeHours) return;

  const data = unNecessaryFEOfficeHourTrans(officeHours);
  if (!data) return "";

  return Object.entries(data)
    .sort(([a], [b]) => DAY_ORDER?.indexOf(a) - DAY_ORDER?.indexOf(b))
    .map(([day, times]) => {
      const startTime = times?.[0]?.open;
      const endTime = times?.[times.length - 1]?.close;

      const formattedTimes = `${toLocalTimeConverter(startTime) ?? ""} - ${
        toLocalTimeConverter(endTime) ?? ""
      }`;

      return `${replaceUnderScoreAddSpace(day)} | ${formattedTimes}`;
    });
};

/**
 * @param {Array<Option>}list
 *
 * @return {Record<string,string>}
 */
export function createMapper(list: Array<Option>) {
  const recordList = list?.reduce(
    (obj, item) => {
      obj[item.value] = item.label;
      return obj;
    },
    {} as Record<string, string>
  );

  return recordList;
}

export const getXtenantId = () => {
  const viewAs = getViewAs();

  return viewAs === "provider" || viewAs === "patient"
    ? (getStoredRealm() ?? getTenantFromHostname() ?? undefined)
    : undefined;
};

export const SECONDARY_TEXT_COLOR = "#1a1a1ab3";

export const getFormattedId = (uuid: any) =>
  typeof uuid === "string" ? uuid.split("-")[0]?.toUpperCase() : "-";

export const getValueFromObj = (obj?: any): string | undefined => {
  if (!obj) return;

  return Object.values(obj)?.[0] as string;
};

/**
 * Joins all values of a dictionary object into a string.
 * Use for multi-value dicts like `{ [uuid]: label }` (e.g. cptCodes, icdCodes).
 * For single-value dicts, prefer `getValueFromObj`.
 */
export const getAllValuesFromObj = (obj?: any, separator = ", "): string => {
  if (!obj || typeof obj !== "object") return "";
  return Object.values(obj).filter(Boolean).join(separator);
};

export const getIdFromObj = (obj?: any): string | undefined => {
  if (!obj) return;

  return Object.keys(obj)?.[0] as string;
};

type ShowToastOnApiCallProps = {
  apiCall: Promise<any>;
  onDelay?: CallableFunction;
  onSave?: CallableFunction;
  showSnackbar: (toast: SnackbarInterface) => void;
};

/**
 * @description
 * - Utility to handle the showToast Controller while call api's
 *
 * @author Rajkumar Rathod
 */
export const showToastOnApiCall = ({
  apiCall,
  onDelay,
  onSave,
  showSnackbar,
}: ShowToastOnApiCallProps) => {
  apiCall
    ?.then((res) => {
      const successMsgToast = getSuccessSnackbar(res);
      successMsgToast?.message && showSnackbar(successMsgToast);
      onSave?.(res);
      setTimeout(() => onDelay?.(), 1500);
    })
    .catch((err) => showSnackbar(getErrorSnackbar(err)));
};

export const setTimeToFixedMidnight = (inputDate = new Date()) => {
  return dayjs(inputDate)
    .startOf("day") // 00:00 local day
    .utc() // convert to UTC
    .toISOString();
};

export const setTimeToFixedAfternight = (inputDate = new Date()) => {
  return dayjs(inputDate).add(1, "day").startOf("day").utc().toISOString();
};

// export const setTimeToFixedMidnight = (inputDate = new Date()) => {
//   const date = new Date(inputDate); // make sure we don't mutate the original

//   const utc = new Date(inputDate).toISOString();
//   console.log("val here check first", inputDate, "latest", utc);

//   return dayjs(date).toISOString();
//   // .set("hour", 0)
//   // .set("minute", 0)
//   // .set("second", 0)
//   // .set("millisecond", 0);
//   // date.setUTCHours(0, 0, 0, 0); // set time to midnight UTC

//   console.log("val here check mid", date);
//   return date.toISOString(); // returns "YYYY-MM-DDT00:00:00.000Z"
// };

// export const setTimeToFixedAfternight = (inputDate = new Date()) => {
//   let date = new Date(inputDate); // make sure we don't mutate the original

//   console.log(
//     "val here checkn",
//     inputDate,
//     "latest",
//     dayjs(date).add(1, "day").toDate().toUTCString()
//   );

//   return dayjs(date).add(1, "day").toISOString();

//   // .set("hour", 0)
//   // .set("minute", 0)
//   // .set("second", 0)
//   // .set("millisecond", 0);
//   // date.setUTCHours(23, 45, 0, 0); // set time to midnight UTC

//   return date.toISOString(); // returns "YYYY-MM-DDT00:00:00.000Z"
// };

/**
 * Used by the dormant ai-assistant feature, which knip ignores.
 * @public
 */
export const getLocalStorageUserDetails = () => {
  const data = sessionStorage.getItem("user") ?? "";
  return JSON.parse(data);
};

export type SelectedItem<T> = {
  value: T;
  label: string;
};

export const getCommaSeparatedValues = (
  arr: { [key: string]: any }[],
  key: string
) => {
  return arr?.map((item) => item[key]).join(",");
};

export const getValuesFromArrayOfObjects = (
  arr: { [key: string]: any }[],
  key: string
) => {
  return arr?.map((item) => item[key]);
};

export function camelCaseToTitle(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (s) => s.toUpperCase());
}

const MIN_SEARCH_KEY_LENGTH = 3;

export const getPageSize = (
  searchKey?: string,
  pagination?: DGT_Pagination
) => {
  return !!searchKey && searchKey?.length >= MIN_SEARCH_KEY_LENGTH
    ? 50
    : pagination?.pageSize;
};

export const getTrimmedUuid = (uuid: string) => {
  return uuid?.split("-")?.[0]?.toUpperCase() ?? "";
};

export const getPatientVisitNoteUrl = (
  patientId: string | number,
  appointmentId: string | number
) =>
  `${PROVIDER_PATHS.NEW_PATIENT}/${patientId}/visit-note/form?appointmentid=${appointmentId}&noteType=PSYCHIATRIC_NOTE&hasEncounterCreation=true`;

/**
 *
 */
type FilterOption = {
  list: Array<any>;
  compWith: Record<string, boolean>;
  removeOtherFields?: boolean;
  k?: string;
};

/**
 *
 * @param list
 *
 * @param compWith {Record<string, boolean>}
 * @returns Filtered list according to compWith
 *
 * @example
 *  const compWith = {
 *   "": true          // allow "Past Medical History"
 *   surgical: false,   // hide "Past Surgical History"
 * };
 *
 *  const visibleTabs = filterList({ list: TABS_DATA, compWith });
 *
 * @author [Rajkumar Rathod]
 */
export const roleFilter = ({
  list,
  compWith,
  removeOtherFields = false,
  k = "value",
}: FilterOption) => {
  return list.filter((it) => {
    const key = String(it[k]);

    if (removeOtherFields) return compWith[key] === true;
    else return compWith[key] !== false;
  });
};

/**
 *
 *
 * @author [Rajkumar Rathod]
 */
export const filterActionCol = <T extends GridValidRowModel>(
  cols: GridColDef<T>[],
  permit: boolean
) => {
  return cols.filter((it) => (permit ? it.field !== "Action" : true));
};

/**
 * @description check if the user is admin
 * @returns boolean
 *
 * @author Rajkumar Rathod
 */
export const isAdmin = () => getViewAs() === "admin";

export const getInitials = (name: string | undefined | null): string => {
  const words =
    name
      ?.trim()
      .split(/\s+/)
      .filter((word) => word.length > 0) || [];
  if (words.length === 0) return "Chat User";
  return words.length === 1
    ? words[0][0].toUpperCase()
    : (words[0][0] + words.at(-1)?.[0]).toUpperCase();
};

export const getLightBgColor = (
  name: string | undefined | null
): { bg: string; text: string } => {
  if (!name) return { bg: "#E3F2FD", text: "#1976D2" };
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return {
    bg: `hsl(${hue}, 70%, 85%)`,
    text: `hsl(${hue}, 70%, 25%)`,
  };
};
export const mergeDateTime = (date?: Date, time?: Date) => {
  if (!date || !time) return;

  const preDate = new Date(date);
  preDate?.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
  return preDate?.toISOString();
};

export function formatFieldName(field: string) {
  if (!field) return "";

  const separated = field
    .replaceAll(".", " ")
    .replaceAll(/([a-z])([A-Z])/g, "$1 $2")
    .replaceAll(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replaceAll(/([a-zA-Z])(\d+)/g, "$1 $2");

  return separated
    .split(" ")
    .map((word) => {
      // if word already looks like abbreviation (all caps)
      if (word === word.toUpperCase() && word.length > 1) {
        return word;
      }

      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ")
    .trim();
}

export function getShortenGender(gender: string): string {
  return gender.charAt(0);
}

export const getUserAlias = (firstName?: string, lastName?: string): string => {
  return `${(firstName ?? "").charAt(0)}${(lastName ?? "").charAt(0)}`.toUpperCase();
};

// Strips leading/duplicate zeros from a numeric string while preserving
// decimals: "00" -> "0", "007" -> "7", "0.5" stays "0.5".
export const stripLeadingZeros = (value: string): string =>
  value.replace(/^0+(?=\d)/, "");
